import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useTrackerPage } from "@hooks/useTrackerPage";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageContainer } from "@components/PageContainer";
import { PageHeader } from "@components/PageHeader";

export default function Tracker() {
  const {
    t,
    entries,
    loading,
    categoriesForForm,
    columns,
    dialogOpen,
    closeDialog,
    editingEntry,
    form,
    setForm,
    deleteId,
    setDeleteId,
    openCreate,
    handleSave,
    handleDelete,
    filteredSubs,
    trackerTab,
    setTrackerTab,
    showKindToggle,
    effectiveFormKind,
    exportCsv,
    trackerCsvInputRef,
    trackerCsvImporting,
    onTrackerCsvFileSelected,
    pickTrackerCsvFile,
  } = useTrackerPage();

  return (
    <PageContainer>
      <input
        ref={trackerCsvInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={onTrackerCsvFileSelected}
      />
      <PageHeader
        title={t("tracker.title")}
        subtitle={t("tracker.subtitle")}
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportCsv}
              disabled={loading || trackerCsvImporting}
            >
              {t("tracker.exportCsv")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={pickTrackerCsvFile}
              disabled={loading || trackerCsvImporting}
            >
              {t("tracker.importCsv")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ bgcolor: "primary.main" }}
            >
              {t("tracker.addTransaction")}
            </Button>
          </Box>
        }
      />
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          value={trackerTab}
          onChange={(_, next) => next != null && setTrackerTab(next)}
          size="small"
          color="secondary"
          aria-label={t("tracker.tabLabel")}
        >
          <ToggleButton value="all">{t("tracker.tabAll")}</ToggleButton>
          <ToggleButton value="expense">
            {t("tracker.tabExpenses")}
          </ToggleButton>
          <ToggleButton value="gain">{t("tracker.tabGains")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Card
        elevation={0}
        sx={{
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={entries}
          columns={columns}
          loading={loading}
          autoHeight
          // Matches MUI's vertical scrollbar gutter when global CSS hides scrollbars (App.css).
          // Measurement returns 0 but the grid still reserves ~14px, which breaks flex column
          // width math between headers and rows.
          scrollbarSize={14}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            borderRadius: 0,
            "& .MuiDataGrid-cell": {
              py: 1.5,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cellContent": {
              display: "flex",
              alignItems: "center",
              width: "100%",
            },
            "& .MuiDataGrid-cell--textRight .MuiDataGrid-cellContent": {
              justifyContent: "flex-end",
            },
            "& .MuiDataGrid-cell--textRight": {
              justifyContent: "flex-end",
            },
            "& .MuiDataGrid-columnHeader": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Card>
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntry ? t("common.edit") : t("tracker.addTransaction")}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          {showKindToggle ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("tracker.transactionKind")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={form.entryKind}
                disabled={!!editingEntry}
                onChange={(_, v) => {
                  if (v == null) return;
                  setForm({
                    ...form,
                    entryKind: v,
                    categoryId: "",
                    subcategoryId: "",
                  });
                }}
                size="small"
                fullWidth
                color="secondary"
              >
                <ToggleButton value="expense">
                  {t("tracker.expensesLabel")}
                </ToggleButton>
                <ToggleButton value="gain">
                  {t("tracker.gainsLabel")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : (
            !editingEntry && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {effectiveFormKind === "gain"
                  ? t("tracker.addUnderGains")
                  : t("tracker.addUnderExpenses")}
              </Typography>
            )
          )}
          <TextField
            label={t("common.name")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label={t("manage.description")}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
          />
          <TextField
            select
            label={t("common.category")}
            value={form.categoryId}
            onChange={(e) =>
              setForm({
                ...form,
                categoryId: e.target.value,
                subcategoryId: "",
              })
            }
            fullWidth
            required
          >
            {categoriesForForm.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("common.subcategory")}
            value={form.subcategoryId}
            onChange={(e) =>
              setForm({ ...form, subcategoryId: e.target.value })
            }
            fullWidth
          >
            <MenuItem value="">--</MenuItem>
            {filteredSubs.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label={t("common.amount")}
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              fullWidth
              required
              slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
            />
            <TextField
              label={t("common.currency")}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              sx={{ width: 120 }}
            />
          </Box>
          <TextField
            label={t("common.date")}
            type="date"
            value={form.spentAt}
            onChange={(e) => setForm({ ...form, spentAt: e.target.value })}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.categoryId || !form.amount}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteId}
        title={t("common.delete")}
        message={t("tracker.deleteEntryConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContainer>
  );
}
